import React, { useState } from 'react';
import { useApp } from '../context/AppContext';
import {
  GraduationCap,
  BarChart3,
  RotateCcw,
  BookOpen,
  LayoutDashboard,
  Layers,
  HelpCircle,
  FileSpreadsheet,
  Sparkles,
  LogOut,
  LogIn,
  User as UserIcon,
  Users as UsersIcon,
  Eye,
  Undo2,
} from 'lucide-react';
import { useToast } from './ui/Toast';
import { ConfirmModal } from './common/ConfirmModal';

export const Header: React.FC = () => {
  const {
    currentUser,
    currentView,
    setCurrentView,
    logoutUser,
    setAuthModalOpen,
    setProfileModalOpen,
    resetToDefaultData,
    attempts,
    returnToAdmin,
  } = useApp();
  const { showToast } = useToast();
  const [showRefreshConfirm, setShowRefreshConfirm] = useState(false);

  const isStudent = !currentUser || currentUser.role === 'student';
  const isAdmin = currentUser?.role === 'admin';

  const handleResetData = () => {
    resetToDefaultData();
    showToast('Refreshed curriculum and questions from database!', 'info', 'Refresh Complete');
    setShowRefreshConfirm(false);
  };

  const handleReturnToAdmin = async () => {
    const res = await returnToAdmin();
    if (res.success) {
      showToast('Restored admin session successfully', 'success', 'Session Restored');
    } else {
      showToast(res.error || 'Failed to return to admin', 'error');
    }
  };

  return (
    <header
      id="app-header"
      className="sticky top-0 z-30 bg-white/95 backdrop-blur-md border-b border-[#E5E9E9] shadow-[0_1px_3px_rgba(17,17,17,0.02)]"
    >
      {/* Impersonation Banner */}
      {currentUser?.is_impersonating && (
        <div className="bg-[#8a6508] text-white px-4 py-2 text-xs font-bold flex items-center justify-between shadow-xs">
          <div className="flex items-center gap-2 max-w-xl truncate">
            <Eye className="w-4 h-4 shrink-0 text-[#FFEAA7]" />
            <span className="truncate">
              Viewing as: <strong className="text-white underline">{currentUser.name}</strong> ({currentUser.email} — {currentUser.role})
            </span>
          </div>
          <button
            onClick={handleReturnToAdmin}
            className="bg-white/20 hover:bg-white/30 text-white px-3 py-1 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer shrink-0 border border-white/40"
          >
            <Undo2 className="w-3.5 h-3.5" />
            Return to Admin
          </button>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 gap-4">
          {/* Brand & Logo */}
          <div className="flex items-center gap-3">
            <button
              id="brand-logo-btn"
              onClick={() => setCurrentView(isStudent ? 'student-dashboard' : 'admin-dashboard')}
              className="flex items-center gap-2.5 text-left group focus:outline-none cursor-pointer"
            >
              <div className="w-10 h-10 rounded-xl bg-[#56E8E1] text-[#111111] flex items-center justify-center font-bold shadow-xs group-hover:scale-105 transition-transform overflow-hidden p-1">
                <img src="/launch_icon.webp" alt="REVise" className="w-full h-full object-contain" />
              </div>
              <div>
                <div className="flex items-center gap-1.5">
                  <span className="text-lg font-black tracking-tight text-[#111111] block leading-tight">
                    Revise
                  </span>
                </div>
                <span className="text-[11px] text-[#5F6368] hidden sm:block font-medium">
                  Exam Revision & Practice
                </span>
              </div>
            </button>
          </div>

          {/* Desktop Navigation Links */}
          <nav className="hidden md:flex items-center gap-1 bg-[#F0F4F4] p-1 rounded-xl border border-[#E5E9E9]">
            {isStudent ? (
              <>
                <button
                  id="nav-student-dashboard"
                  onClick={() => setCurrentView('student-dashboard')}
                  className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-all flex items-center gap-1.5 cursor-pointer ${
                    currentView === 'student-dashboard'
                      ? 'bg-white text-[#111111] shadow-xs border border-[#E5E9E9] font-bold'
                      : 'text-[#5F6368] hover:text-[#111111] hover:bg-white/60'
                  }`}
                >
                  <BookOpen className="w-4 h-4 text-[#1fb4ac]" />
                  Home
                </button>
                <button
                  id="nav-student-sets"
                  onClick={() => setCurrentView('student-sets')}
                  className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-all flex items-center gap-1.5 cursor-pointer ${
                    currentView === 'student-sets'
                      ? 'bg-white text-[#111111] shadow-xs border border-[#E5E9E9] font-bold'
                      : 'text-[#5F6368] hover:text-[#111111] hover:bg-white/60'
                  }`}
                >
                  <Sparkles className="w-4 h-4 text-[#1fb4ac]" />
                  Practice
                </button>
                <button
                  id="nav-student-revision"
                  onClick={() => setCurrentView('quiz-revision')}
                  className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-all flex items-center gap-1.5 cursor-pointer ${
                    currentView === 'quiz-revision'
                      ? 'bg-white text-[#111111] shadow-xs border border-[#E5E9E9] font-bold'
                      : 'text-[#5F6368] hover:text-[#111111] hover:bg-white/60'
                  }`}
                >
                  <RotateCcw className="w-4 h-4 text-[#1fb4ac]" />
                  REVise
                </button>
                <button
                  id="nav-student-analytics"
                  onClick={() => setCurrentView('student-analytics')}
                  className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-all flex items-center gap-1.5 cursor-pointer ${
                    currentView === 'student-analytics' || currentView === 'quiz-scorecard'
                      ? 'bg-white text-[#111111] shadow-xs border border-[#E5E9E9] font-bold'
                      : 'text-[#5F6368] hover:text-[#111111] hover:bg-white/60'
                  }`}
                >
                  <BarChart3 className="w-4 h-4 text-[#1fb4ac]" />
                  Progress
                  {attempts.length > 0 && (
                    <span className="ml-1 px-1.5 py-0.2 rounded-full bg-[#E8FAF9] text-[#1b8e88] text-[10px] font-bold">
                      {attempts.length}
                    </span>
                  )}
                </button>
                <button
                  id="nav-student-account"
                  onClick={() => setCurrentView('student-account')}
                  className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-all flex items-center gap-1.5 cursor-pointer ${
                    currentView === 'student-account'
                      ? 'bg-white text-[#111111] shadow-xs border border-[#E5E9E9] font-bold'
                      : 'text-[#5F6368] hover:text-[#111111] hover:bg-white/60'
                  }`}
                >
                  <UserIcon className="w-4 h-4 text-[#1fb4ac]" />
                  Account
                </button>
              </>
            ) : (
              <>
                <button
                  id="nav-admin-dashboard"
                  onClick={() => setCurrentView('admin-dashboard')}
                  className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-all flex items-center gap-1.5 cursor-pointer ${
                    currentView === 'admin-dashboard'
                      ? 'bg-white text-[#111111] shadow-xs border border-[#E5E9E9] font-bold'
                      : 'text-[#5F6368] hover:text-[#111111] hover:bg-white/60'
                  }`}
                >
                  <LayoutDashboard className="w-3.5 h-3.5 text-[#1fb4ac]" />
                  Overview
                </button>
                <button
                  id="nav-admin-users"
                  onClick={() => setCurrentView('admin-users')}
                  className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-all flex items-center gap-1.5 cursor-pointer ${
                    currentView === 'admin-users'
                      ? 'bg-white text-[#111111] shadow-xs border border-[#E5E9E9] font-bold'
                      : 'text-[#5F6368] hover:text-[#111111] hover:bg-white/60'
                  }`}
                >
                  <UsersIcon className="w-3.5 h-3.5 text-[#1fb4ac]" />
                  Users
                </button>
                <button
                  id="nav-admin-questions"
                  onClick={() => setCurrentView('admin-questions')}
                  className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-all flex items-center gap-1.5 cursor-pointer ${
                    currentView === 'admin-questions'
                      ? 'bg-white text-[#111111] shadow-xs border border-[#E5E9E9] font-bold'
                      : 'text-[#5F6368] hover:text-[#111111] hover:bg-white/60'
                  }`}
                >
                  <HelpCircle className="w-3.5 h-3.5 text-[#1fb4ac]" />
                  Questions
                </button>
                <button
                  id="nav-admin-sets"
                  onClick={() => setCurrentView('admin-sets')}
                  className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-all flex items-center gap-1.5 cursor-pointer ${
                    currentView === 'admin-sets'
                      ? 'bg-white text-[#111111] shadow-xs border border-[#E5E9E9] font-bold'
                      : 'text-[#5F6368] hover:text-[#111111] hover:bg-white/60'
                  }`}
                >
                  <Layers className="w-3.5 h-3.5 text-[#1fb4ac]" />
                  Question Sets
                </button>
                <button
                  id="nav-admin-sectors"
                  onClick={() => setCurrentView('admin-sectors')}
                  className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-all flex items-center gap-1.5 cursor-pointer ${
                    currentView === 'admin-sectors' || currentView === 'admin-subjects'
                      ? 'bg-white text-[#111111] shadow-xs border border-[#E5E9E9] font-bold'
                      : 'text-[#5F6368] hover:text-[#111111] hover:bg-white/60'
                  }`}
                >
                  <Sparkles className="w-3.5 h-3.5 text-[#1fb4ac]" />
                  Subjects
                </button>
                <button
                  id="nav-admin-attempts"
                  onClick={() => setCurrentView('admin-attempts')}
                  className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-all flex items-center gap-1.5 cursor-pointer ${
                    currentView === 'admin-attempts'
                      ? 'bg-white text-[#111111] shadow-xs border border-[#E5E9E9] font-bold'
                      : 'text-[#5F6368] hover:text-[#111111] hover:bg-white/60'
                  }`}
                >
                  <FileSpreadsheet className="w-3.5 h-3.5 text-[#1fb4ac]" />
                  Results
                </button>
              </>
            )}
          </nav>

          {/* Right Action Controls */}
          <div className="flex items-center gap-2 sm:gap-2.5">
            {/* Reset Demo Data Button */}
            <button
              id="reset-demo-data-btn"
              onClick={handleResetData}
              className="p-2 text-[#5F6368] hover:text-[#111111] hover:bg-[#F0F4F4] rounded-xl border border-transparent hover:border-[#E5E9E9] transition-all cursor-pointer"
              title="Refresh data from database"
            >
              <RotateCcw className="w-4 h-4" />
            </button>

            {currentUser ? (
              <>
                {/* Current User Profile Chip */}
                <button
                  id="user-profile-btn"
                  onClick={() => setProfileModalOpen(true)}
                  className="flex items-center gap-2 pl-1.5 pr-2.5 py-1 rounded-xl border border-[#E5E9E9] hover:border-[#56E8E1] hover:bg-[#E8FAF9]/40 transition-all text-left group cursor-pointer"
                >
                  <div className="w-7 h-7 rounded-lg bg-[#E8FAF9] text-[#1b8e88] font-bold text-xs flex items-center justify-center border border-[#56E8E1]/40 group-hover:scale-105 transition-transform overflow-hidden">
                    {currentUser.profile_picture ? (
                      <img src={currentUser.profile_picture} alt={currentUser.name} className="w-full h-full object-cover" />
                    ) : currentUser.name ? (
                      currentUser.name.charAt(0)
                    ) : (
                      <UserIcon className="w-4 h-4" />
                    )}
                  </div>
                  <div className="hidden lg:block">
                    <div className="text-xs font-bold text-[#111111] leading-tight">
                      {currentUser.name ? currentUser.name.split(' ')[0] : 'User'}
                    </div>
                    <div className="text-[10px] text-[#5F6368] font-medium capitalize">
                      {currentUser.role}
                    </div>
                  </div>
                </button>

                {/* Real Logout Button */}
                <button
                  id="logout-btn"
                  onClick={() => {
                    logoutUser();
                    showToast('Signed out successfully', 'info');
                  }}
                  className="p-2 text-[#5F6368] hover:text-[#D94A5C] hover:bg-[#FDEDEF] rounded-xl border border-transparent hover:border-[#D94A5C]/30 transition-all cursor-pointer flex items-center gap-1 text-xs font-semibold"
                  title="Sign Out"
                >
                  <LogOut className="w-4 h-4" />
                  <span className="hidden sm:inline">Sign Out</span>
                </button>
              </>
            ) : (
              <button
                id="header-login-btn"
                onClick={() => setAuthModalOpen(true)}
                className="px-3.5 py-1.5 bg-[#56E8E1] hover:bg-[#34d5cd] text-[#111111] font-bold text-xs rounded-xl shadow-xs transition-all flex items-center gap-1.5 cursor-pointer"
              >
                <LogIn className="w-4 h-4" />
                Sign In
              </button>
            )}
          </div>
        </div>
      </div>
      <ConfirmModal
        isOpen={showRefreshConfirm}
        title="Refresh Data"
        message="Refresh application data from database?"
        confirmText="Refresh"
        variant="info"
        onConfirm={handleResetData}
        onCancel={() => setShowRefreshConfirm(false)}
      />
    </header>
  );
};
